import { hubLiveStreamApiUrl } from "./hubApiOrigin";
import { HUB_CLIENT_BUILD_VERSION } from "./hubBuildVersion";
import { getHubLiveTransportDiagnostics } from "./hubLiveBroker";
import { HUB_LIVE_STREAM_PATH } from "./hubLiveChannelUnion";
import { getApiFetchAdmissionSnapshot } from "./apiFetchAdmission";
import { getApiFetchTimelineSnapshot } from "./apiFetchTelemetry";

export type NetworkProbeResult = {
  url: string;
  outcome: "response" | "timeout" | "error";
  status: number | null;
  elapsedMs: number;
  nextHopProtocol: string | null;
  error: string | null;
};

function resourceProtocol(url: string): string | null {
  const entries = performance.getEntriesByName(url, "resource") as PerformanceResourceTiming[];
  return entries.at(-1)?.nextHopProtocol || null;
}

async function probe(url: string, timeoutMs = 3_000): Promise<NetworkProbeResult> {
  const startedAt = performance.now();
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort("diagnostic-timeout"), timeoutMs);
  try {
    const response = await fetch(url, {
      cache: "no-store",
      credentials: "include",
      signal: controller.signal,
    });
    return {
      url,
      outcome: "response",
      status: response.status,
      elapsedMs: Math.round(performance.now() - startedAt),
      nextHopProtocol: resourceProtocol(url),
      error: null,
    };
  } catch (error) {
    const timedOut = controller.signal.aborted;
    return {
      url,
      outcome: timedOut ? "timeout" : "error",
      status: null,
      elapsedMs: Math.round(performance.now() - startedAt),
      nextHopProtocol: resourceProtocol(url),
      error: error instanceof Error ? error.message : String(error),
    };
  } finally {
    window.clearTimeout(timer);
  }
}

export async function collectSlowRequestNetworkDiagnostics(): Promise<Record<string, unknown>> {
  const streamUrl = hubLiveStreamApiUrl(HUB_LIVE_STREAM_PATH);
  const liveOrigin = new URL(streamUrl, window.location.href).origin;
  const mainProbeUrl = new URL("/healthz", window.location.origin).toString();
  const liveProbeUrl = new URL("/healthz", liveOrigin).toString();
  const navigation = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
  const [mainOriginProbe, liveOriginProbe] = await Promise.all([
    probe(mainProbeUrl),
    liveOrigin === window.location.origin ? Promise.resolve(null) : probe(liveProbeUrl),
  ]);
  return {
    clientBuildVersion: HUB_CLIENT_BUILD_VERSION,
    documentOrigin: window.location.origin,
    documentProtocol: navigation?.nextHopProtocol || null,
    liveStreamUrl: streamUrl,
    liveStreamOrigin: liveOrigin,
    liveStreamSeparated: liveOrigin !== window.location.origin,
    liveTransport: getHubLiveTransportDiagnostics(),
    mainOriginProbe,
    liveOriginProbe,
    restAdmission: getApiFetchAdmissionSnapshot(),
    restRequests: getApiFetchTimelineSnapshot(),
    online: navigator.onLine,
    visibilityState: document.visibilityState,
  };
}
